import React, { useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  LineChart,
  Line,
  Legend,
  ScatterChart,
  Scatter,
  ZAxis,
  AreaChart,
  Area,
  FunnelChart,
  Funnel,
  LabelList
} from "recharts";
import RecentActivities from "../../features/dashboard/recentActivities";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SyncIcon from '@mui/icons-material/Sync';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import BarChartIcon from '@mui/icons-material/BarChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import SearchBar from "../../components/SearchBar";

const dashboardTabs = [
  "Foundations",
  "Performance",
  "Operations",
  "Growth",
  "Predictive",
  "Strategic",
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
  >
    {value === index && <Box sx={{ p: 0, pt: 2 }}>{children}</Box>}
  </div>
);

const a11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
  "aria-controls": `simple-tabpanel-${index}`,
});

const dauData = [
  { name: "Mon", users: 120 },
  { name: "Tue", users: 210 },
  { name: "Wed", users: 180 },
  { name: "Thu", users: 300 },
  { name: "Fri", users: 260 },
  { name: "Sat", users: 140 },
  { name: "Sun", users: 170 },
];

const revenueOrdersData = [
  { name: "Week 1", revenue: 4100, orders: 240 },
  { name: "Week 2", revenue: 3000, orders: 139 },
  { name: "Week 3", revenue: 2000, orders: 1000 },
  { name: "Week 4", revenue: 2780, orders: 380 },
  { name: "Week 5", revenue: 1890, orders: 480 },
];

const periodComparisonData = [
  { name: "This Month", revenue: 45000, target: 51000 },
  { name: "Last Month", revenue: 37000, target: 45000 },
  { name: "Q1 Average", revenue: 41000, target: 49000 },
];

const underperformingProducts = [
  { name: 'Basic USB Hub', sales: 45, reason: 'Low demand', change: '-15%' },
  { name: 'Keyboard Wrist Rest', sales: 32, reason: 'Price too high', change: '-22%' },
  { name: 'Monitor Riser', sales: 28, reason: 'Seasonal decline', change: '-18%' },
];

const operationalAlerts = [
  { title: 'Low Stock Alert', desc: 'Premium Headphones (12 units left)', icon: <Inventory2OutlinedIcon fontSize="small" />, color: '#f97316', bg: '#fff7ed', border: '#fed7aa' },
  { title: 'Order Delays', desc: '3 orders exceed SLA by 24 hours', icon: <AccessTimeOutlinedIcon fontSize="small" />, color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  { title: 'High Cancellation', desc: '8% cancellation rate (up 2%)', icon: <TrendingDownIcon fontSize="small" />, color: '#f97316', bg: '#fff7ed', border: '#fed7aa' },
  { title: 'Customer Complaints', desc: 'Shipping quality concerns (5 reports)', icon: <ChatBubbleOutlineIcon fontSize="small" />, color: '#eab308', bg: '#fefce8', border: '#fef08a' },
];

const funnelData = [
  { value: 12450, name: "Store Views", fill: "#8b5cf6" },
  { value: 8500, name: "Product Views", fill: "#3b82f6" },
  { value: 3200, name: "Add to Cart", fill: "#10b981" },
  { value: 1450, name: "Checkout Initiated", fill: "#f59e0b" },
  { value: 890, name: "Orders Completed", fill: "#ef4444" }
];

const aiInsights = [
  { title: 'Improve Product Visibility', desc: 'USB Hub has low traffic. Increase ad spend or optimize SEO', action: 'Optimize' },
  { title: 'Price Adjustment', desc: 'Monitor Riser is overpriced. Reduce by 15% to boost sales', action: 'Adjust' },
  { title: 'Bundle Strategy', desc: 'Create bundle: Headphones + Cable for better conversion', action: 'Create' },
];

const campaignPerformanceData = [
  { name: "5400", value: 245 },
  { name: "8200", value: 190 },
  { name: "3100", value: 312 },
  { name: "12000", value: 178 },
  { name: "6800", value: 235 },
];

const customerSegmentsData = [
  { title: "High-Value", customers: 245, spend: "$2,450", change: "+18%", positive: true },
  { title: "Regular", customers: 1240, spend: "$450", change: "+8%", positive: true },
  { title: "At-Risk", customers: 340, spend: "$120", change: "-12%", positive: false },
  { title: "New", customers: 520, spend: "$280", change: "+35%", positive: true },
];

const crossSellOpportunities = [
  { primary: "Premium Headphones", secondary: "→ Phone Case", revenue: "$8,500", conversion: "34% conversion" },
  { primary: "Laptop Stand", secondary: "→ Desk Lamp", revenue: "$6,200", conversion: "28% conversion" },
  { primary: "USB Cable", secondary: "→ Power Bank", revenue: "$5,800", conversion: "25% conversion" },
  { primary: "Monitor", secondary: "→ Monitor Stand", revenue: "$7,200", conversion: "31% conversion" },
];

const growthIntelligenceData = [
  { title: "Product Mix Optimization", desc: "Increase high-margin products in premium segment", metric: "+12% Revenue" },
  { title: "Geographic Expansion", desc: "Strong demand in EU region, expand inventory", metric: "+8% Growth" },
  { title: "Seasonal Bundling", desc: "Holiday bundles perform 45% better than single items", metric: "+22% Q4" },
];

const pricingSuggestionsData = [
  { product: "Luxury Silk Scarf", currentPrice: "$120", suggestedPrice: "$145", reason: "High demand, low price elasticity", impact: "+15% Margin" },
  { product: "Basic Cotton T-Shirt", currentPrice: "$35", suggestedPrice: "$29", reason: "Competitor price drop detected", impact: "+25% Volume" },
  { product: "Leather Crossbody Bag", currentPrice: "$250", suggestedPrice: "$275", reason: "Premium positioning opportunity", impact: "+10% Margin" },
  { product: "Summer Hat", currentPrice: "$45", suggestedPrice: "$39", reason: "End of season clearance", impact: "Clear Inventory" }
];

const demandForecastData = [
  { month: "Jan", value: 4200 },
  { month: "Feb", value: 3300 },
  { month: "Mar", value: 2000 },
  { month: "Apr", value: 2800 },
  { month: "May", value: 1800 },
];

const revenuePredictionData = [
  { day: "1", revenue: 420 },
  { day: "5", revenue: 580 },
  { day: "10", revenue: 650 },
  { day: "15", revenue: 720 },
  { day: "20", revenue: 850 },
  { day: "25", revenue: 940 },
  { day: "30", revenue: 1100 },
];

const churnRiskData = [
  { customer: "Customer #2341", reason: "No purchase in 60 days", risk: "92%", action: "Re-engage", color: "#fee2e2", textColor: "#ef4444" },
  { customer: "Customer #2340", reason: "Declining avg order value", risk: "78%", action: "Offer Discount", color: "#ffedd5", textColor: "#f97316" },
  { customer: "Customer #2339", reason: "Low engagement rate", risk: "65%", action: "Win-back Email", color: "#ffedd5", textColor: "#f97316" },
];

const inventoryForecastingData = [
  { product: "Premium Headphones", current: "Current: 245 units", predicted: "Predicted (30d): 180", out: "Out: Mar 15", status: "monitor", color: "#ffedd5", textColor: "#f97316" },
  { product: "USB Cable", current: "Current: 520 units", predicted: "Predicted (30d): 120", out: "Out: Feb 28", status: "critical", color: "#fee2e2", textColor: "#ef4444" },
  { product: "Monitor Stand", current: "Current: 180 units", predicted: "Predicted (30d): 150", out: "Out: Apr 10", status: "ok", color: "#dcfce7", textColor: "#22c55e" },
];

const salesOpportunityData = [
  { deal: "Enterprise Deal A", amount: "$45,000", stage: "Negotiation", probability: 82 },
  { deal: "SMB Deal B", amount: "$12,000", stage: "Proposal", probability: 65 },
  { deal: "Startup Deal C", amount: "$8,000", stage: "Discovery", probability: 45 },
];

const earlyWarningSignalsData = [
  { message: "Revenue decline predicted in Week 2 - Recommend promotional campaign", color: "#fef2f2", textColor: "#ef4444", borderColor: "#fca5a5" },
  { message: "Top product will stock out by Feb 28 - Increase inventory orders", color: "#fff7ed", textColor: "#f97316", borderColor: "#fdba74" },
  { message: "Customer retention declining - Launch loyalty program", color: "#fefce8", textColor: "#eab308", borderColor: "#fde047" },
];

const strategicGrowthPlansData = [
  {
    title: "Revenue Diversification Initiative",
    duration: "6-8 weeks",
    desc: "Expand product portfolio with 3 new premium categories based on market demand analysis.",
    impact: "Projected 18% revenue increase Q2-Q3",
    steps: ["Market research", "Supplier sourcing", "Inventory setup", "Marketing launch"],
  },
  {
    title: "Customer Retention Program",
    duration: "3-4 weeks",
    desc: "Implement AI-driven loyalty program targeting 35% repeat customer rate increase.",
    impact: "Reduce churn by 15%, increase lifetime value by 22%",
    steps: ["Program design", "Tech integration", "Staff training", "Campaign launch"],
  }
];

const whatIfAnalysisData = [
  { action: "Increase Price by 10%", impact: "Revenue: +8%, Orders: -4%, Margin: +12%", recommendation: "Good for premium segment", recommendationType: "warning" },
  { action: "Launch Loyalty Program", impact: "Retention: +22%, CAC: -8%, LTV: +35%", recommendation: "Highly recommended - strong ROI", recommendationType: "success" },
  { action: "Enter New Market (EU)", impact: "Revenue: +18%, Market share: +3%, Risk: Medium", recommendation: "Viable with proper planning", recommendationType: "info" },
  { action: "20% Ad Spend Increase", impact: "Reach: +45%, Conversions: +28%, ROI: 3.2x", recommendation: "Strong performance expected", recommendationType: "warning" },
];

const aiAutonomousRecommendationsData = [
  { priority: "Priority 1: Launch Loyalty Program", desc: "Expected ROI: 3.2x in 6 months. Start implementation immediately.", icon: "✅", bg: "#f0fdf4", border: "#bbf7d0", iconColor: "#16a34a" },
  { priority: "Priority 2: Expand Product Mix", desc: "Market demand indicates 3 new categories. Partner with suppliers by March.", icon: "💡", bg: "#eff6ff", border: "#bfdbfe", iconColor: "#ca8a04" },
  { priority: "Priority 3: International Expansion", desc: "EU market entry feasible Q2. Medium risk with proper planning. Projected 18% revenue increase.", icon: "🚀", bg: "#faf5ff", border: "#e9d5ff", iconColor: "#9333ea" },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [valueTabs, setValueTabs] = useState<number>(0);

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };
  const [searchTerm, setSearchTerm] = useState("");

  return (
    <>
      <div className="main_title d_flex">
        <h1>Dashboard</h1>
        <div className="right_s">
          <SearchBar
            searchTerm={searchTerm}
            setDebouncedSearchTerm={setSearchTerm}
            placeholder="AI Search"
          />
        </div>
      </div>

      <Box className="custom_tabs1" sx={{ width: "100%", mt: 2 }}>
        <Tabs
          value={valueTabs}
          onChange={handleChangeTabs}
          aria-label="dashboard tabs"
          className="custom_tabs_links"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {dashboardTabs.map((tab, index) => (
            <Tab key={tab} label={tab} {...a11yProps(index)} />
          ))}
        </Tabs>
      </Box>

      {dashboardTabs.map((tab, index) => (
        <CustomTabPanel key={index} value={valueTabs} index={index}>
          {index === 0 ? (
            <>
              <div className="stats_boxes gap_m">
                {/* New KPI Cards for Core Business Snapshot */}
                <div className="cards">
                  <figure className="sucess">
                    <img src="/static/images/reward_icon.svg" alt="Revenue Summary" />
                  </figure>
                  <h2>AED 15,400</h2>
                  <p>Revenue summary</p>
                  <div className="rating_box">
                    <p><span><ArrowUpwardIcon />+12%</span>this month</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="warning">
                    <img src="/static/images/bag.svg" alt="Active Products" />
                  </figure>
                  <h2>18</h2>
                  <p>Active products/services</p>
                  <div className="rating_box">
                    <p><span><ArrowUpwardIcon />+2</span>this month</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="blue">
                    <img src="/static/images/active_order.svg" alt="Customer Count" />
                  </figure>
                  <h2>342</h2>
                  <p>Basic customer count</p>
                  <div className="rating_box">
                    <p><span><ArrowUpwardIcon />+15</span>this month</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="warning">
                    <img src="/static/images/star.svg" alt="Pending Actions" />
                  </figure>
                  <h2>5</h2>
                  <p>Pending actions</p>
                  <div className="rating_box">
                    <p><span style={{ color: '#666', background: 'transparent', padding: 0 }}>3 orders, 2 messages</span></p>
                  </div>
                </div>
              </div>

              <section>
                <div className="table_group gap_m table_card mt_20">
                  <div className="cards table_card w_50">
                    <div className="sub_hd">
                      <h3>Recent Store Activities</h3>
                      <a href="/">View All Activities</a>
                    </div>
                    {/* <RecentActivities /> */}
                    <ul className="order_dtls">
                      <li>
                        <figure className="green">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                            data-slot="icon"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
                            ></path>
                          </svg>
                        </figure>
                        <h6>
                          New Order <span className=" d_block">Traditional Abaya</span>
                        </h6>
                        <p>
                          {" "}
                          2 hours ago <span className="green d_block">+AED 150</span>
                        </p>
                      </li>
                      <li>
                        <figure className="yellow">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                            data-slot="icon"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                            ></path>
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                            ></path>
                          </svg>
                        </figure>
                        <h6>
                          Product Viewedr{" "}
                          <span className=" d_block">Silk Scarf Collection</span>
                        </h6>
                        <p>
                          {" "}
                          2 hours ago <span className="warning d_block">23 views</span>
                        </p>
                      </li>
                      <li>
                        <figure className="purple">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                            data-slot="icon"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                            ></path>
                          </svg>
                        </figure>
                        <h6>
                          New Order <span className=" d_block">Traditional Abaya</span>
                        </h6>
                        <p> 2 hours ago </p>
                      </li>
                    </ul>
                  </div>


                  <div className="cards store_list w_50">
                    <div className="sub_hd">
                      <h3>Recent Orders</h3>
                      <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <select style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', outline: 'none', cursor: 'pointer' }}>
                          <option value="today">Orders Today</option>
                          <option value="this_week">Orders This Week</option>
                        </select>
                        <a href="/">View All Orders</a>
                      </div>
                    </div>
                    <ul className="order_dtls ">
                      <li>
                        <figure className="green">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                            data-slot="icon"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
                            ></path>
                          </svg>
                        </figure>
                        <h6>
                          #ORD-001 <span className=" d_block">Fatima Al-Zahra • Traditional Abaya</span>
                        </h6>
                        <p>
                          {" "}
                          2 hours ago <span className="green d_block">+AED 150</span>
                        </p>
                      </li>
                      <li>
                        <figure className="yellow">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                            data-slot="icon"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                            ></path>
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                            ></path>
                          </svg>
                        </figure>
                        <h6>
                          #ORD-002{" "}
                          <span className=" d_block">Aisha Mohammed • Silk Scarf</span>
                        </h6>
                        <p>
                          {" "}
                          2 hours ago <span className="warning d_block">23 views</span>
                        </p>
                      </li>
                      <li>
                        <figure className="purple">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                            data-slot="icon"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                            ></path>
                          </svg>
                        </figure>
                        <h6>
                          #ORD-003 <span className=" d_block">Mariam Hassan • Pearl Necklace</span>
                        </h6>
                        <p> 2 hours ago </p>
                      </li>
                      <li>
                        <figure className="purple">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                            data-slot="icon"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                            ></path>
                          </svg>
                        </figure>
                        <h6>
                          #ORD-004 <span className=" d_block">Noura Ahmed • Designer Handbag</span>
                        </h6>
                        <p> 2 hours ago </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>
            </>
          ) : index === 1 ? (
            <>
              <div className="stats_boxes gap_m">
                <div className="cards">
                  <figure className="sucess">
                    <TrendingUpIcon />
                  </figure>
                  <h2>2.8%</h2>
                  <p>Conversion Rate</p>
                  <div className="rating_box">
                    <p><span><ArrowUpwardIcon />+0.3%</span>vs last period</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="warning">
                    <SyncIcon />
                  </figure>
                  <h2>35.2%</h2>
                  <p>Repeat Customer Rate</p>
                  <div className="rating_box">
                    <p><span><ArrowUpwardIcon />+2.1%</span>Returning customers</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="blue">
                    <LocalOfferIcon />
                  </figure>
                  <h2>$156.80</h2>
                  <p>Avg Order Value</p>
                  <div className="rating_box">
                    <p><span><ArrowUpwardIcon />+8.5%</span>Per transaction</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="purple">
                    <RemoveRedEyeOutlinedIcon />
                  </figure>
                  <h2>12,450</h2>
                  <p>Store views</p>
                  <div className="rating_box">
                    <p><span><ArrowUpwardIcon />+15.2%</span>vs last period</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="warning">
                    <StarOutlineIcon />
                  </figure>
                  <h2>4.8</h2>
                  <p>Customer Reviews</p>
                  <div className="rating_box">
                    <p><span><ArrowUpwardIcon />+120</span>new reviews</p>
                  </div>
                </div>
              </div>

              <div className="grid_sc gap_m mt_20">
                {/* Daily Active Users Chart */}
                <div className="cards table_card">
                  <div className="cards_header">
                    <div className="left_s">
                      <h2>Last 7 months performance</h2>
                    </div>
                    <div className="right_s">
                      <p>
                        <span>A day</span>
                        <span>A week</span>
                        <span>A month</span>
                        <span>A year</span>
                      </p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dauData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="users" fill="#D3B88C" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="cards table_card">
                  <div className="cards_header">
                    <div className="left_s">
                      <h2>Last 1 year performance</h2>
                    </div>
                    <div className="right_s">
                      <p>
                        <span>A day</span>
                        <span>A week</span>
                        <span>A month</span>
                        <span>A year</span>
                      </p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dauData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="users" fill="#D3B88C" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="cards table_card mt_20">
                <div className="cards_header">
                  <div className="left_s">
                    <h2>Revenue & Orders Trends</h2>
                    <p style={{ color: '#999', fontSize: '14px', marginTop: '5px' }}>Weekly performance comparison</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={revenueOrdersData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" axisLine={{ stroke: '#e0e0e0' }} tickLine={false} tick={{ fill: '#666', fontSize: 13 }} dy={10} />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: '#666', fontSize: 13 }} dx={-10} />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fill: '#666', fontSize: 13 }} dx={10} />
                    <Tooltip wrapperStyle={{ outline: 'none' }} contentStyle={{ borderRadius: '8px', border: '1px solid #eee', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                    <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                    <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#8b5cf6" strokeWidth={2} activeDot={{ r: 8 }} dot={{ strokeWidth: 2, r: 4, fill: '#fff' }} />
                    <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#3b82f6" strokeWidth={2} dot={{ strokeWidth: 2, r: 4, fill: '#fff' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="table_group gap_m table_card mt_20">
                <div className="cards table_card" style={{ width: '100%' }}>
                  <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                    <div>
                      <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Top Performing Products</h3>
                      <p style={{ color: '#999', fontSize: '13px' }}>Based on sales and revenue</p>
                    </div>
                  </div>
                  <ul className="order_dtls">
                    <li style={{ padding: '15px 0' }}>
                      <figure style={{ width: '40px', height: '40px', background: '#f5f5f5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#333' }}>
                        #1
                      </figure>
                      <h6 style={{ fontSize: '15px', fontWeight: 600 }}>
                        Premium Wireless Headphones <span className="d_block" style={{ fontSize: '13px', color: '#999', fontWeight: 400, marginTop: '4px' }}>1250 sales</span>
                      </h6>
                      <p style={{ textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '15px' }}>
                        $24,500 <span className="d_block" style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '4px' }}>★ 4.8</span>
                      </p>
                    </li>
                    <li style={{ padding: '15px 0' }}>
                      <figure style={{ width: '40px', height: '40px', background: '#f5f5f5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#333' }}>
                        #2
                      </figure>
                      <h6 style={{ fontSize: '15px', fontWeight: 600 }}>
                        USB-C Cable Bundle <span className="d_block" style={{ fontSize: '13px', color: '#999', fontWeight: 400, marginTop: '4px' }}>980 sales</span>
                      </h6>
                      <p style={{ textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '15px' }}>
                        $18,200 <span className="d_block" style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '4px' }}>★ 4.6</span>
                      </p>
                    </li>
                    <li style={{ padding: '15px 0' }}>
                      <figure style={{ width: '40px', height: '40px', background: '#f5f5f5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#333' }}>
                        #3
                      </figure>
                      <h6 style={{ fontSize: '15px', fontWeight: 600 }}>
                        Laptop Stand Pro <span className="d_block" style={{ fontSize: '13px', color: '#999', fontWeight: 400, marginTop: '4px' }}>756 sales</span>
                      </h6>
                      <p style={{ textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '15px' }}>
                        $15,120 <span className="d_block" style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '4px' }}>★ 4.7</span>
                      </p>
                    </li>
                    <li style={{ padding: '15px 0' }}>
                      <figure style={{ width: '40px', height: '40px', background: '#f5f5f5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#333' }}>
                        #4
                      </figure>
                      <h6 style={{ fontSize: '15px', fontWeight: 600 }}>
                        Phone Case Set <span className="d_block" style={{ fontSize: '13px', color: '#999', fontWeight: 400, marginTop: '4px' }}>645 sales</span>
                      </h6>
                      <p style={{ textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '15px' }}>
                        $12,900 <span className="d_block" style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '4px' }}>★ 4.5</span>
                      </p>
                    </li>
                    <li style={{ padding: '15px 0' }}>
                      <figure style={{ width: '40px', height: '40px', background: '#f5f5f5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#333' }}>
                        #5
                      </figure>
                      <h6 style={{ fontSize: '15px', fontWeight: 600 }}>
                        Screen Protector Pack <span className="d_block" style={{ fontSize: '13px', color: '#999', fontWeight: 400, marginTop: '4px' }}>523 sales</span>
                      </h6>
                      <p style={{ textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '15px' }}>
                        $10,460 <span className="d_block" style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '4px' }}>★ 4.4</span>
                      </p>
                    </li>
                  </ul>
                </div>

                {/* <div className="cards table_card" style={{ width: '100%' }}>
                  <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                    <div>
                      <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Period Comparison</h3>
                      <p style={{ color: '#999', fontSize: '13px' }}>Revenue vs targets</p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={periodComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" axisLine={{ stroke: '#e0e0e0' }} tickLine={false} tick={{ fill: '#666', fontSize: 13 }} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#666', fontSize: 13 }} dx={-10} ticks={[0, 15000, 30000, 45000, 60000]} />
                      <Tooltip wrapperStyle={{ outline: 'none' }} cursor={{ fill: '#f5f5f5' }} contentStyle={{ borderRadius: '8px', border: '1px solid #eee', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                      <Legend iconType="square" wrapperStyle={{ paddingTop: '20px' }} />
                      <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={40} />
                      <Bar dataKey="target" fill="#10b981" radius={[4, 4, 0, 0]} barSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </div> */}
              </div>
            </>
          ) : index === 2 ? (
            <>
              <div className="stats_boxes gap_m">
                <div className="cards">
                  <figure className="sucess">
                    <BarChartIcon />
                  </figure>
                  <h2>1240</h2>
                  <p>High View</p>
                  <div className="rating_box">
                    <p>Premium Headphones, Cables</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="warning">
                    <BarChartIcon />
                  </figure>
                  <h2>340</h2>
                  <p>Low View</p>
                  <div className="rating_box">
                    <p>Keyboard Wrist Rest, Monitor Riser</p>
                  </div>
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Basic Funnel View</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>Customer journey drop-off analysis</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={funnelData} layout="vertical" margin={{ top: 20, right: 50, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f0f0f0" />
                    <XAxis type="number" hide />
                    <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fill: '#666', fontSize: 13 }} width={130} />
                    <Tooltip cursor={{ fill: '#f5f5f5' }} contentStyle={{ borderRadius: '8px', border: '1px solid #eee', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                    <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={32}>
                      <LabelList dataKey="value" position="right" fill="#666" fontSize={13} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Underperforming Products</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>Products below expected performance</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {underperformingProducts.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      background: '#fffcfc'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <WarningAmberIcon style={{ color: '#ef4444' }} />
                        <div>
                          <h6 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: 0 }}>{item.name}</h6>
                          <p style={{ fontSize: '13px', color: '#999', margin: '4px 0 0 0' }}>{item.sales} sales &bull; {item.reason}</p>
                        </div>
                      </div>
                      <div style={{ fontWeight: 'bold', color: '#ef4444', fontSize: '15px' }}>
                        {item.change}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Operational Alerts</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>Critical issues requiring attention</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {operationalAlerts.map((alert, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px',
                      border: `1px solid ${alert.border}`,
                      borderRadius: '8px',
                      background: alert.bg,
                      color: alert.color
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {alert.icon}
                        </div>
                        <div>
                          <h6 style={{ fontSize: '15px', fontWeight: 600, color: alert.color, margin: 0 }}>{alert.title}</h6>
                          <p style={{ fontSize: '13px', color: alert.color, margin: '4px 0 0 0', opacity: 0.8 }}>{alert.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>AI Insights & Recommendations</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>Smart suggestions to improve performance</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {aiInsights.map((insight, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      border: '1px solid #f1f5f9',
                      borderRadius: '8px',
                      background: '#fff'
                    }}>
                      <div>
                        <h6 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: 0 }}>{insight.title}</h6>
                        <p style={{ fontSize: '13px', color: '#999', margin: '4px 0 0 0' }}>{insight.desc}</p>
                      </div>
                      <div style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 500 }}>
                        {insight.action}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </>
          ) : index === 3 ? (
            <>
              <div className="stats_boxes gap_m">
                <div className="cards">
                  <figure className="sucess">
                    <TrendingUpIcon />
                  </figure>
                  <h2>+24%</h2>
                  <p>Growth Opportunity</p>
                  <div className="rating_box">
                    <p>Potential revenue increase with optimization</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="warning">
                    <ShoppingCartOutlinedIcon />
                  </figure>
                  <h2>$27.7K</h2>
                  <p>Cross-sell Potential</p>
                  <div className="rating_box">
                    <p>Untapped cross-sell revenue</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="blue">
                    <WorkspacePremiumOutlinedIcon />
                  </figure>
                  <h2>87%</h2>
                  <p>Segment Health</p>
                  <div className="rating_box">
                    <p>Overall customer satisfaction</p>
                  </div>
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Campaign Performance</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>ROI and reach analysis</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={campaignPerformanceData} margin={{ top: 20, right: 30, bottom: 20, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={true} stroke="#f0f0f0" />
                    <XAxis dataKey="name" axisLine={{ stroke: '#e0e0e0' }} tickLine={false} tick={{ fill: '#666', fontSize: 13 }} dy={10} />
                    <YAxis axisLine={{ stroke: '#e0e0e0' }} tickLine={true} tick={{ fill: '#666', fontSize: 13 }} ticks={[0, 80, 160, 240, 320]} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} wrapperStyle={{ outline: 'none' }} contentStyle={{ borderRadius: '8px', border: '1px solid #eee', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                    <Line type="linear" dataKey="value" stroke="#6366f1" strokeWidth={2} dot={{ r: 4, fill: '#6366f1', strokeWidth: 0 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Customer Segments</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>Segment performance and growth</p>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  {customerSegmentsData.map((segment, i) => (
                    <div key={i} style={{
                      border: '1px solid #f1f5f9',
                      borderRadius: '8px',
                      padding: '20px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '24px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <h6 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: '0 0 4px 0' }}>{segment.title}</h6>
                          <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>{segment.customers} customers</p>
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: segment.positive ? '#10b981' : '#ef4444' }}>
                          {segment.change}
                        </span>
                      </div>
                      <div>
                        <h4 style={{ fontSize: '20px', fontWeight: 700, color: '#111', margin: '0 0 4px 0' }}>{segment.spend}</h4>
                        <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>Avg spend</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Products Pricing Suggestions</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>AI-driven recommendations for optimal pricing</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {pricingSuggestionsData.map((suggestion, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      border: '1px solid #f1f5f9',
                      borderRadius: '8px',
                      background: '#fff'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h6 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: '0 0 4px 0' }}>{suggestion.product}</h6>
                        <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
                          Current: <span style={{ textDecoration: 'line-through', marginRight: '8px' }}>{suggestion.currentPrice}</span>
                          Suggested: <span style={{ fontWeight: 600, color: '#10b981' }}>{suggestion.suggestedPrice}</span>
                        </p>
                        <p style={{ fontSize: '12px', color: '#999', margin: '4px 0 0 0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <AutoAwesomeIcon fontSize="inherit" style={{ color: '#8b5cf6' }} /> {suggestion.reason}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background: suggestion.impact.includes('+') ? '#dcfce7' : '#f1f5f9',
                          color: suggestion.impact.includes('+') ? '#15803d' : '#475569',
                          fontSize: '13px',
                          fontWeight: 500
                        }}>
                          {suggestion.impact}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Cross-Sell & Upsell Opportunities</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>High-potential product combinations</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {crossSellOpportunities.map((opportunity, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      border: '1px solid #f1f5f9',
                      borderRadius: '8px',
                      background: '#fff'
                    }}>
                      <div>
                        <h6 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: 0 }}>{opportunity.primary}</h6>
                        <p style={{ fontSize: '13px', color: '#cbd5e1', margin: '4px 0 0 0' }}>{opportunity.secondary}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <h6 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: 0 }}>{opportunity.revenue}</h6>
                        <p style={{ fontSize: '13px', color: '#cbd5e1', margin: '4px 0 0 0' }}>{opportunity.conversion}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Growth Intelligence</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>Strategic growth opportunities</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {growthIntelligenceData.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      padding: '16px',
                      border: '1px solid #f1f5f9',
                      borderRadius: '8px',
                      background: '#fff'
                    }}>
                      <div style={{ color: '#cbd5e1', paddingTop: '2px' }}>
                        <ElectricBoltIcon fontSize="small" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h6 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: 0 }}>{item.title}</h6>
                        <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 8px 0' }}>{item.desc}</p>
                        <span style={{
                          display: 'inline-block',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#64748b',
                          background: '#f8fafc',
                          padding: '2px 8px',
                          borderRadius: '4px'
                        }}>
                          {item.metric}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : index === 4 ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <div style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <WorkspacePremiumOutlinedIcon style={{ color: '#ef4444', fontSize: '18px' }} />
                  <span style={{ fontSize: '14px', color: '#b91c1c', fontWeight: 600 }}>Upgrade to unlock Predictive Insights</span>
                </div>
              </div>

              <div className="stats_boxes gap_m">
                <div className="cards">
                  <figure className="sucess">
                    <PsychologyOutlinedIcon />
                  </figure>
                  <h2>+18%</h2>
                  <p>Demand Forecast</p>
                  <div className="rating_box">
                    <p>Expected increase next quarter</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="warning">
                    <WarningAmberOutlinedIcon />
                  </figure>
                  <h2>12.3%</h2>
                  <p>Churn Risk</p>
                  <div className="rating_box">
                    <p>High-risk customer segment</p>
                  </div>
                </div>

                <div className="cards">
                  <figure className="blue">
                    <BoltOutlinedIcon />
                  </figure>
                  <h2>$58K</h2>
                  <p>Revenue 90-Day</p>
                  <div className="rating_box">
                    <p>Projected Q2 revenue</p>
                  </div>
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Demand Forecasting</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>Product-level trend projection</p>
                  </div>
                  <div>
                    <select style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', outline: 'none', cursor: 'pointer', fontSize: '13px' }}>
                      <option value="all">All Products</option>
                      <option value="premium_headphones">Premium Headphones</option>
                      <option value="usb_cable">USB Cable</option>
                      <option value="monitor_stand">Monitor Stand</option>
                    </select>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={demandForecastData} margin={{ top: 20, right: 30, bottom: 20, left: -20 }}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={true} stroke="#f0f0f0" />
                    <XAxis dataKey="month" axisLine={{ stroke: '#e0e0e0' }} tickLine={false} tick={{ fill: '#666', fontSize: 13 }} dy={10} />
                    <YAxis axisLine={{ stroke: '#e0e0e0' }} tickLine={true} tick={{ fill: '#666', fontSize: 13 }} ticks={[0, 1500, 3000, 4500, 6000]} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} wrapperStyle={{ outline: 'none' }} contentStyle={{ borderRadius: '8px', border: '1px solid #eee', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                    <Area type="monotone" dataKey="value" stroke="#3b82f6" fillOpacity={1} fill="url(#colorValue)" strokeWidth={2} />
                    <Area type="monotone" dataKey={() => 4300} stroke="#3b82f6" strokeDasharray="3 3" fill="none" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Revenue Prediction</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>Expected revenue trend</p>
                  </div>
                  <div>
                    <select style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', outline: 'none', cursor: 'pointer', fontSize: '13px' }}>
                      <option value="30">Next 30 Days</option>
                      <option value="90">Next 90 Days</option>
                    </select>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenuePredictionData} margin={{ top: 20, right: 30, bottom: 20, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={true} stroke="#f0f0f0" />
                    <XAxis dataKey="day" axisLine={{ stroke: '#e0e0e0' }} tickLine={false} tick={{ fill: '#666', fontSize: 13 }} dy={10} />
                    <YAxis axisLine={{ stroke: '#e0e0e0' }} tickLine={true} tick={{ fill: '#666', fontSize: 13 }} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} wrapperStyle={{ outline: 'none' }} contentStyle={{ borderRadius: '8px', border: '1px solid #eee', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                    <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#10b981', strokeWidth: 0 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Churn Risk Prediction</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>Customers at risk of leaving</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {churnRiskData.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      border: '1px solid #f1f5f9',
                      borderRadius: '8px',
                      background: '#fff'
                    }}>
                      <div>
                        <h6 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: 0 }}>{item.customer}</h6>
                        <p style={{ fontSize: '13px', color: '#999', margin: '4px 0 0 0' }}>{item.reason}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: item.textColor,
                          background: item.color,
                          padding: '4px 12px',
                          borderRadius: '16px'
                        }}>
                          {item.risk}
                        </span>
                        <span style={{ fontSize: '13px', color: '#e2e8f0', width: '100px', textAlign: 'right', fontWeight: 500 }}>
                          {item.action}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Inventory Forecasting</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>Predictive stock-out alerts</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {inventoryForecastingData.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      border: '1px solid #f1f5f9',
                      borderRadius: '8px',
                      background: '#fff'
                    }}>
                      <div>
                        <h6 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: 0 }}>{item.product}</h6>
                        <p style={{ fontSize: '13px', color: '#999', margin: '4px 0 0 0' }}>{item.current}</p>
                        <p style={{ fontSize: '13px', color: '#999', margin: '4px 0 0 0' }}>
                          {item.predicted} <span style={{ color: '#ef4444', fontWeight: 600, marginLeft: '12px' }}>{item.out}</span>
                        </p>
                      </div>
                      <div>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: item.textColor,
                          background: item.color,
                          padding: '4px 12px',
                          borderRadius: '16px'
                        }}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>Sales Opportunity Probability</h3>
                    <p style={{ color: '#999', fontSize: '13px' }}>AI-powered deal scoring</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {salesOpportunityData.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      padding: '16px',
                      border: '1px solid #f1f5f9',
                      borderRadius: '8px',
                      background: '#fff'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h6 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: 0 }}>{item.deal}</h6>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#e2e8f0' }}>{item.probability}%</span>
                      </div>
                      <div style={{ width: '100%', height: '6px', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${item.probability}%`, height: '100%', backgroundColor: '#f4f4f5' }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>{item.amount}</p>
                        <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>{item.stage}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ElectricBoltIcon style={{ color: '#f1f5f9' }} />
                    <div>
                      <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>AI Early-Warning Signals</h3>
                      <p style={{ color: '#999', fontSize: '13px' }}>Predictive alerts for business threats</p>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {earlyWarningSignalsData.map((alert, i) => (
                    <div key={i} style={{
                      padding: '16px',
                      background: alert.color,
                      borderRadius: '8px',
                      border: `1px solid ${alert.borderColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <WarningAmberIcon style={{ color: alert.textColor, fontSize: '20px' }} />
                      <p style={{ margin: 0, color: alert.textColor, fontSize: '14px', fontWeight: 500 }}>
                        {alert.message}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : index === 5 ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginTop: '20px' }}>
                <div className="cards table_card" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <AutoAwesomeIcon style={{ color: '#f1f5f9', fontSize: '24px' }} />
                    <h3 style={{ fontSize: '18px', margin: 0 }}>AI Weekly Business Brief</h3>
                  </div>
                  <p style={{ color: '#999', fontSize: '13px', marginBottom: '16px' }}>Weekly Business Brief - Feb 17-24, 2024</p>

                  <p style={{ fontSize: '14px', color: '#333', lineHeight: '1.6', marginBottom: '24px' }}>
                    Strong week with 12% revenue growth. Customer acquisition costs decreased while retention improved. This week showed exceptional performance across key indicators, particularly in the high-value customer segment which grew by 18%. The success of your recent bundling strategy has contributed to improved average order values.
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                    <div style={{ padding: '16px', border: '1px solid #f1f5f9', borderRadius: '8px' }}>
                      <p style={{ color: '#999', fontSize: '13px', margin: '0 0 8px 0' }}>Revenue Growth</p>
                      <h4 style={{ color: '#10b981', fontSize: '20px', margin: 0, fontWeight: 700 }}>+12%</h4>
                    </div>
                    <div style={{ padding: '16px', border: '1px solid #f1f5f9', borderRadius: '8px' }}>
                      <p style={{ color: '#999', fontSize: '13px', margin: '0 0 8px 0' }}>New Customers</p>
                      <h4 style={{ color: '#10b981', fontSize: '20px', margin: 0, fontWeight: 700 }}>345</h4>
                    </div>
                    <div style={{ padding: '16px', border: '1px solid #f1f5f9', borderRadius: '8px' }}>
                      <p style={{ color: '#999', fontSize: '13px', margin: '0 0 8px 0' }}>Churn Rate</p>
                      <h4 style={{ color: '#10b981', fontSize: '20px', margin: 0, fontWeight: 700 }}>-2.1%</h4>
                    </div>
                    <div style={{ padding: '16px', border: '1px solid #f1f5f9', borderRadius: '8px' }}>
                      <p style={{ color: '#999', fontSize: '13px', margin: '0 0 8px 0' }}>Avg Order Value</p>
                      <h4 style={{ color: '#10b981', fontSize: '20px', margin: 0, fontWeight: 700 }}>+8.5%</h4>
                    </div>
                  </div>
                </div>

                <div className="cards table_card" style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                    <PsychologyOutlinedIcon style={{ color: '#64748b' }} />
                    <h3 style={{ fontSize: '16px', margin: 0, color: '#64748b' }}>AI Confidence Score</h3>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>Forecast Accuracy</span>
                      <span style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: 600 }}>94%</span>
                    </div>
                    <div style={{ width: '100%', height: '8px', backgroundColor: '#f8fafc', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: '94%', height: '100%', backgroundColor: '#10b981', borderRadius: '4px' }} />
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>Data Quality</span>
                      <span style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: 600 }}>89%</span>
                    </div>
                    <div style={{ width: '100%', height: '8px', backgroundColor: '#f8fafc', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: '89%', height: '100%', backgroundColor: '#10b981', borderRadius: '4px' }} />
                    </div>
                  </div>

                  <p style={{ fontSize: '13px', color: '#64748b', margin: 0, borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                    Model: Enterprise AI v4.2
                  </p>
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TrendingUpOutlinedIcon style={{ color: '#f1f5f9' }} />
                    <h3 style={{ fontSize: '18px', margin: 0 }}>Strategic Growth Plans</h3>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {strategicGrowthPlansData.map((plan, i) => (
                    <div key={i} style={{ border: '1px solid #f1f5f9', borderRadius: '12px', padding: '24px', background: '#fff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#333', margin: '0 0 8px 0' }}>{plan.title}</h4>
                          <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>{plan.desc}</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f8fafc', padding: '4px 12px', borderRadius: '16px' }}>
                          <BarChartIcon style={{ fontSize: '14px', color: '#cbd5e1' }} />
                          <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>{plan.duration}</span>
                        </div>
                      </div>

                      <div style={{ background: '#fafafa', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '16px', margin: '20px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <BarChartIcon style={{ fontSize: '16px', color: '#3b82f6' }} />
                          <span style={{ fontSize: '14px', fontWeight: 600, color: '#f1f5f9' }}>Expected Impact</span>
                        </div>
                        <p style={{ fontSize: '14px', color: '#333', margin: 0 }}>{plan.impact}</p>
                      </div>

                      <div>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: '#94a3b8', margin: '0 0 12px 0' }}>Implementation Steps</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                          {plan.steps.map((step, stepIndex) => (
                            <div key={stepIndex} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{
                                width: '24px', height: '24px',
                                background: '#e2e8f0', color: '#475569',
                                borderRadius: '50%', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: '12px', fontWeight: 700
                              }}>
                                {stepIndex + 1}
                              </div>
                              <span style={{ fontSize: '13px', color: '#64748b' }}>{step}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LightbulbOutlinedIcon style={{ color: '#f1f5f9' }} />
                    <h3 style={{ fontSize: '18px', margin: 0 }}>What-If Analysis</h3>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                  {whatIfAnalysisData.map((analysis, i) => (
                    <div key={i} style={{ border: '1px solid #f1f5f9', borderRadius: '12px', padding: '24px', background: '#fff' }}>
                      <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: '0 0 20px 0' }}>{analysis.action}</h4>

                      <div style={{ border: '1px solid #f1f5f9', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                        <p style={{ fontSize: '12px', color: '#94a3b8', margin: '0 0 8px 0' }}>Impact</p>
                        <p style={{ fontSize: '14px', fontWeight: 500, color: '#333', margin: 0 }}>{analysis.impact}</p>
                      </div>

                      <div style={{
                        background: analysis.recommendationType === 'success' ? '#dcfce7' : analysis.recommendationType === 'warning' ? '#f3e8ff' : '#eff6ff',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {analysis.recommendationType === 'success' ? (
                          <span style={{ color: '#16a34a', fontSize: '13px', fontWeight: 500 }}>✓ {analysis.recommendation}</span>
                        ) : analysis.recommendationType === 'warning' ? (
                          <span style={{ color: '#9333ea', fontSize: '13px', fontWeight: 500 }}>✓ {analysis.recommendation}</span>
                        ) : (
                          <span style={{ color: '#3b82f6', fontSize: '13px', fontWeight: 500 }}>✓ {analysis.recommendation}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cards table_card mt_20" style={{ width: '100%', marginTop: '20px' }}>
                <div className="sub_hd" style={{ paddingBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AutoAwesomeIcon style={{ color: '#333', fontSize: '20px' }} />
                    <h3 style={{ fontSize: '18px', margin: 0 }}>AI Autonomous Recommendations</h3>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {aiAutonomousRecommendationsData.map((rec, i) => (
                    <div key={i} style={{
                      background: rec.bg,
                      border: `1px solid ${rec.border}`,
                      borderRadius: '8px',
                      padding: '20px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px'
                    }}>
                      <div style={{
                        width: '28px', height: '28px',
                        background: '#fff',
                        borderRadius: '4px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: `1px solid ${rec.border}`,
                        fontSize: '16px'
                      }}>
                        {rec.icon === '✅' ? <CheckCircleIcon style={{ color: '#22c55e', fontSize: '20px' }} /> : rec.icon}
                      </div>
                      <div>
                        <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: '0 0 8px 0' }}>{rec.priority}</h4>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>{rec.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </>
          ) : (
            <div className="cards table_card" style={{ textAlign: "center", padding: "40px" }}>
              <h2>{tab} Details Coming Soon...</h2>
            </div>
          )}
        </CustomTabPanel>
      ))}

      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
        <p style={{ fontSize: '12px', color: '#888', margin: 0, fontStyle: 'italic' }}>
          AI Insights are guidance generated from available marketplace data and may not always be accurate. Please review before applying.
        </p>
      </Box>
    </>
  );
};

export default Dashboard;
